import { useEffect, useState } from 'react'
import {
  Box,
  Flex,
  Heading,
  Container,
  HStack,
  Stack,
  Text,
  Link,
  Icon,
  useColorModeValue,
  SlideFade,
} from '@chakra-ui/react'
import { FiGithub, FiLinkedin } from 'react-icons/fi'
import { SiServerless, SiServerlessHex } from '@icons-pack/react-simple-icons'
import {
  TypescriptOriginal,
  JavascriptOriginal,
  NestjsOriginal,
  ExpressOriginal,
  PythonOriginal,
  DjangoPlain,
  FastapiOriginal,
  AmazonwebservicesOriginalWordmark,
  NodejsOriginal,
  RabbitmqOriginal,
  RedisOriginal,
  PostgresqlOriginal,
  MongodbOriginal,
  JavaOriginal,
} from 'devicons-react'
import '../index.css'

// macOS traffic light button
const WindowButton = ({ color }) => (
  <Box w="12px" h="12px" borderRadius="full" bg={color} />
)

// All entries use multi-color devicons-react `Original` components,
// except Serverless (not in devicons) which uses simple-icons mono + hex.
const TECH_STACK = [
  { name: 'TypeScript', icon: TypescriptOriginal },
  { name: 'JavaScript', icon: JavascriptOriginal },
  { name: 'NestJS', icon: NestjsOriginal },
  { name: 'Express', icon: ExpressOriginal },
  { name: 'Python', icon: PythonOriginal },
  { name: 'Django', icon: DjangoPlain },
  { name: 'FastAPI', icon: FastapiOriginal },
  { name: 'AWS', icon: AmazonwebservicesOriginalWordmark },
  {
    name: 'Serverless',
    icon: SiServerless,
    mono: true,
    color: SiServerlessHex,
  },
  { name: 'Node.js', icon: NodejsOriginal },
  { name: 'RabbitMQ', icon: RabbitmqOriginal },
  { name: 'Redis', icon: RedisOriginal },
  { name: 'PostgreSQL', icon: PostgresqlOriginal },
  { name: 'MongoDB', icon: MongodbOriginal },
  { name: 'Java', icon: JavaOriginal },
]

const ROTATION_MS = 2000

export default function Header() {
  // Dracula theme colors
  const bgColor = useColorModeValue('#f8f8f2', '#282a36')
  const headerBg = useColorModeValue('#e8e8e8', '#44475a')
  const textColor = useColorModeValue('#282a36', '#f8f8f2')
  const subtitleColor = useColorModeValue('#6272a4', '#bd93f9')
  const promptColor = useColorModeValue('#6272a4', '#ff79c6')
  const keywordColor = useColorModeValue('#6272a4', '#8be9fd')
  const linkColor = useColorModeValue('#6272a4', '#50fa7b')
  const borderColor = useColorModeValue('#d1d1d1', '#44475a')
  const pillBg = useColorModeValue('#eeeeee', '#3b3d4d')
  const pillBorder = useColorModeValue('#dcdcdc', '#4d5066')

  const [techIndex, setTechIndex] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setTechIndex(i => (i + 1) % TECH_STACK.length)
    }, ROTATION_MS)
    return () => clearInterval(id)
  }, [])

  const currentTech = TECH_STACK[techIndex]

  return (
    <>
      <Flex flex="1" direction="column">
        {/* Hero (full-bleed, no code block) */}
        <Container maxW="80ch" pt={{ base: '6rem', md: '8rem' }}>
          <Box textAlign="center" px={{ base: 2, md: 4 }}>
            <Heading
              fontWeight={500}
              fontSize={{ base: 32, md: 56 }}
              lineHeight="110%"
              fontFamily="mono"
              color={textColor}
            >
              <span className="wave"> 👋</span> Hey, I'm Nemanja Ilic
            </Heading>
            <Text
              mt="4"
              fontFamily="mono"
              fontSize={{ base: 'md', md: 'lg' }}
              color={subtitleColor}
            >
              Software Engineer
            </Text>
            <HStack
              mt="5"
              spacing="3"
              justify="center"
              fontFamily="mono"
              fontSize={{ base: 'md', md: 'lg' }}
              color={textColor}
              minH="2.5rem"
            >
              <Text as="span" whiteSpace="nowrap">
                Building stuff using
              </Text>
              <Box
                w={{ base: '12rem', md: '14rem' }}
                h="2.25rem"
                position="relative"
              >
                <SlideFade
                  key={techIndex}
                  in={true}
                  offsetY="10px"
                  transition={{ enter: { duration: 0.35 } }}
                  style={{ position: 'absolute', inset: 0 }}
                >
                  <HStack
                    spacing="2"
                    px="3"
                    py="1"
                    borderRadius="md"
                    bg={pillBg}
                    border="1px solid"
                    borderColor={pillBorder}
                    boxShadow="sm"
                    w="100%"
                    h="100%"
                    justify="center"
                    overflow="hidden"
                  >
                    {currentTech.mono ? (
                      <Icon
                        as={currentTech.icon}
                        boxSize={5}
                        color={currentTech.color}
                        flexShrink={0}
                      />
                    ) : (
                      <Box as={currentTech.icon} size="1.5rem" flexShrink={0} />
                    )}
                    <Text
                      as="span"
                      fontWeight="600"
                      color="white"
                      whiteSpace="nowrap"
                    >
                      {currentTech.name}
                    </Text>
                  </HStack>
                </SlideFade>
              </Box>
            </HStack>
          </Box>
        </Container>

        <Container maxW="60ch">
          <Stack
            as={Box}
            spacing={{ base: 8, md: 14 }}
            pt={{ base: '3rem', md: '5rem' }}
            pb={{ base: 30, md: '5rem' }}
            direction="column"
          >
            {/* About macOS window */}
            <Box
              borderRadius="lg"
              overflow="hidden"
              boxShadow="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Flex
                bg={headerBg}
                px="4"
                py="3"
                alignItems="center"
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <HStack spacing="2" mr="4">
                  <WindowButton color="#ff5f56" />
                  <WindowButton color="#ffbd2e" />
                  <WindowButton color="#27c93f" />
                </HStack>
                <Text
                  fontFamily="mono"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                >
                  about.js
                </Text>
              </Flex>
              <Box bg={bgColor} p="4" fontFamily="mono">
                <Text
                  fontWeight={300}
                  fontSize={{ md: 'large' }}
                  color={textColor}
                >
                  <Text as="span" color={promptColor}>
                    &gt;
                  </Text>{' '}
                  <Text as="span" color={keywordColor}>
                    const
                  </Text>{' '}
                  me = {'{'}'
                  <Text as="span" color={linkColor}>
                    Software engineer
                  </Text>
                  ', '
                  <Text as="span" color={linkColor}>
                    tech enthusiast
                  </Text>
                  '{'}'}
                </Text>
              </Box>
            </Box>

            <Box justifyContent="center">
              <Stack
                as={HStack}
                marginY="5"
                spacing="6"
                justify="center"
                direction="row"
              >
                <Link
                  href="https://github.com/neilic023"
                  isExternal
                  color={linkColor}
                  aria-label="GitHub"
                  _hover={{ opacity: 0.8 }}
                >
                  <Icon as={FiGithub} boxSize={8} />
                </Link>
                <Link
                  href="https://linkedin.com/in/ilkedevs"
                  isExternal
                  color={linkColor}
                  aria-label="LinkedIn"
                  _hover={{ opacity: 0.8 }}
                >
                  <Icon as={FiLinkedin} boxSize={8} />
                </Link>
              </Stack>

              {/* Contact macOS window */}
              <Box
                borderRadius="lg"
                overflow="hidden"
                boxShadow="lg"
                border="1px solid"
                borderColor={borderColor}
                mt="6"
              >
                <Flex
                  bg={headerBg}
                  px="4"
                  py="3"
                  alignItems="center"
                  borderBottom="1px solid"
                  borderColor={borderColor}
                >
                  <HStack spacing="2" mr="4">
                    <WindowButton color="#ff5f56" />
                    <WindowButton color="#ffbd2e" />
                    <WindowButton color="#27c93f" />
                  </HStack>
                  <Text
                    fontFamily="mono"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                  >
                    contact.sh
                  </Text>
                </Flex>
                <Box bg={bgColor} p="4" fontFamily="mono">
                  <Text color={textColor}>
                    <Text as="span" color={promptColor}>
                      &gt;
                    </Text>{' '}
                    echo "Get in touch 👉"
                  </Text>
                  <Text color={textColor} mt="1">
                    <Text as="span" color={promptColor}>
                      &gt;
                    </Text>{' '}
                    <Link
                      href="mailto:nemanja.ilic@protonmail.com"
                      color={linkColor}
                    >
                      nemanja.ilic@protonmail.com
                    </Link>
                  </Text>
                </Box>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Flex>
    </>
  )
}
